import React, { useState, FC } from 'react';
import { FormControl, OutlinedInput, FormHelperText, ToggleButton, ToggleButtonGroup } from '@mui/material';
import TextFieldInput from 'components/TextFieldInput/TextFieldInput';
import ValueTags from 'components/ValueTags/ValueTags';
import { processInputValue, createEnterKeyHandler } from '../utils';

// This will no longer need to support URL patterns cause it will use a different component/field for that.

/**
 * @module CampaignBulkAddField
 * @description A component that allows users to add multiple values either individually or in bulk
 * through a text input or textarea. Commonly used for URL patterns and search phrases.
 */

/**
 * @typedef {'URL Patterns' | 'Search Phrases'} BulkAddFieldTitle
 * The allowed title values for the bulk add field
 */

/**
 * @interface ICampaignBulkAddField
 * @property {string[]} valueArray - Array of currently added values
 * @property {(value: string) => void} handleAddValue - Callback function to add a new value
 * @property {(index: number) => void} handleRemoveValue - Callback function to remove a value at specified index
 * @property {boolean} error - Whether the field is in an error state
 * @property {BulkAddFieldTitle} title - The title of the field, either 'URL Patterns' or 'Search Phrases'
 * @property {string} placeholder - Placeholder text for the input field
 * @property {boolean} [disable] - Whether the field is disabled
 */

interface ICampaignBulkAddField {
  valueArray: string[];
  handleAddValue: (value: string) => void;
  handleRemoveValue: (index: number) => void;
  error: boolean;
  title: 'URL Patterns' | 'Search Phrases';
  placeholder: string;
  disable?: boolean;
}

const CampaignBulkAddField: FC<ICampaignBulkAddField> = ({
  valueArray,
  handleAddValue,
  handleRemoveValue,
  error,
  title,
  placeholder,
  disable = false,
}) => {
  const [value, setValue] = useState('');
  const [formMode, setFormMode] = useState<'single' | 'bulk'>('single');

  const isUrlPatterns = title === 'URL Patterns';
  const testIdPrefix = isUrlPatterns ? 'url-pattern' : 'search-phrase';
  const errorText = isUrlPatterns
    ? 'Please add at least one valid URL pattern and/or search phrase'
    : 'Please add at least one search phrase and/or URL pattern';

  const handleOnAdd = (inputValue: string) => {
    processInputValue(inputValue, formMode, handleAddValue, () => setValue(''));
  };

  const handleKeyDown = createEnterKeyHandler(() => handleOnAdd(value));

  const renderAddButton = () => (
    <button
      type="button"
      className="px-4 py-2 font-bold text-white rounded-md bg-muted-dark-purple hover:bg-dark-purple"
      onClick={() => handleOnAdd(value)}
      data-testid={`add-${testIdPrefix}-button`}
      disabled={disable}
    >
      Add
    </button>
  );

  const renderBulkAddField = () => (
    <div className="flex flex-col">
      <TextFieldInput
        label=""
        value={value}
        handleChange={setValue}
        hasError={error}
        placeholderText={placeholder}
        rows={3}
        isDisabled={disable}
        onKeyDown={handleKeyDown}
        errorText={errorText}
      />
      <div className="flex justify-end">{renderAddButton()}</div>
    </div>
  );

  const renderSingleAddField = () => (
    <div>
      <FormControl className="flex gap-2 mb-2" error={error}>
        <div className="flex gap-2">
          <OutlinedInput
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
            placeholder={placeholder}
            data-testid={`${testIdPrefix}-input`}
            disabled={disable}
          />
          {!disable && renderAddButton()}
        </div>
        {error && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-start mb-4">
        <h5 className="text-muted-dark-purple mr-4">{title}</h5>
        {!disable && (
          <ToggleButtonGroup value={formMode} onChange={(_, value) => setFormMode(value)} exclusive size="small">
            <ToggleButton value="single" className="normal-case font-montserrat text-xs py-1">
              Single
            </ToggleButton>
            <ToggleButton value="bulk" className="normal-case font-montserrat text-xs py-1">
              Bulk
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </div>

      {formMode === 'bulk' ? renderBulkAddField() : renderSingleAddField()}
      {valueArray.length > 0 && (
        <ValueTags
          valueArray={valueArray}
          handleRemoveValue={handleRemoveValue}
          disable={disable}
          getDisplayText={(value: string) => value}
        />
      )}
    </div>
  );
};

export default CampaignBulkAddField;
